/**
 * useWeatherForecast.js — 16-day daily forecast for 5 shoot locations
 *
 * Uses Open-Meteo free API (no key required).
 * Returns forecast[cityName][dateStr] = { code, high, low, wind }
 * Also exports matchCity() to fuzzy-match job location → city name.
 * Auto-refreshes every 30 minutes.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const LOCATIONS = [
  { name: 'Santa Barbara', lat: 34.4208, lon: -119.6982 },
  { name: 'Ventura',       lat: 34.2746, lon: -119.2290 },
  { name: 'Santa Monica',  lat: 34.0195, lon: -118.4912 },
  { name: 'Solvang',       lat: 34.5958, lon: -120.1376 },
  { name: 'SLO',           lat: 35.2828, lon: -120.6596 },
];

// WMO Weather interpretation codes → description + icon
const WMO_CODES = {
  0:  { desc: 'Clear',          icon: '☀️' },
  1:  { desc: 'Mostly Clear',   icon: '🌤️' },
  2:  { desc: 'Partly Cloudy',  icon: '⛅' },
  3:  { desc: 'Overcast',       icon: '☁️' },
  45: { desc: 'Foggy',          icon: '🌫️' },
  48: { desc: 'Rime Fog',       icon: '🌫️' },
  51: { desc: 'Light Drizzle',  icon: '🌦️' },
  53: { desc: 'Drizzle',        icon: '🌦️' },
  55: { desc: 'Heavy Drizzle',  icon: '🌧️' },
  61: { desc: 'Light Rain',     icon: '🌦️' },
  63: { desc: 'Rain',           icon: '🌧️' },
  65: { desc: 'Heavy Rain',     icon: '🌧️' },
  71: { desc: 'Light Snow',     icon: '🌨️' },
  73: { desc: 'Snow',           icon: '🌨️' },
  75: { desc: 'Heavy Snow',     icon: '🌨️' },
  80: { desc: 'Rain Showers',   icon: '🌧️' },
  81: { desc: 'Mod. Showers',   icon: '🌧️' },
  82: { desc: 'Heavy Showers',  icon: '🌧️' },
  95: { desc: 'Thunderstorm',   icon: '⛈️' },
  96: { desc: 'T-Storm + Hail', icon: '⛈️' },
  99: { desc: 'Heavy T-Storm',  icon: '⛈️' },
};

export function weatherInfo(code) {
  return WMO_CODES[code] || { desc: 'Unknown', icon: '❓' };
}

// Keyword → city name mapping for fuzzy location match
const CITY_KEYWORDS = [
  { keywords: ['santa barbara', 'goleta', 'montecito', 'carpinteria', 'summerland', 'isla vista'], city: 'Santa Barbara' },
  { keywords: ['ventura', 'ojai', 'oxnard', 'camarillo', 'thousand oaks'], city: 'Ventura' },
  { keywords: ['santa monica', 'malibu', 'los angeles', 'la', 'beverly hills', 'culver', 'west hollywood', 'brentwood', 'pacific palisades'], city: 'Santa Monica' },
  { keywords: ['solvang', 'buellton', 'lompoc', 'santa ynez', 'los olivos', 'los alamos'], city: 'Solvang' },
  { keywords: ['slo', 'san luis obispo', 'paso robles', 'pismo', 'arroyo grande', 'morro bay', 'atascadero'], city: 'SLO' },
];

/** Match a job location string to the nearest known city. Defaults to Santa Barbara. */
export function matchCity(locationStr) {
  if (!locationStr) return 'Santa Barbara';
  const lower = locationStr.toLowerCase();
  for (const { keywords, city } of CITY_KEYWORDS) {
    for (const kw of keywords) {
      if (lower.includes(kw)) return city;
    }
  }
  return 'Santa Barbara';
}

export default function useWeatherForecast() {
  // { cityName: { 'YYYY-MM-DD': { code, high, low, wind } } }
  const [forecast, setForecast] = useState({});
  const [loading, setLoading] = useState(true);
  const fetchRef = useRef(false);

  const fetchForecast = useCallback(async () => {
    if (fetchRef.current) return;
    fetchRef.current = true;
    try {
      setLoading(true);
      const lats = LOCATIONS.map(l => l.lat).join(',');
      const lons = LOCATIONS.map(l => l.lon).join(',');
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lats}&longitude=${lons}&daily=weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America/Los_Angeles&forecast_days=16`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      // Open-Meteo returns an array when multiple locations are requested
      const results = Array.isArray(data) ? data : [data];

      const map = {};
      LOCATIONS.forEach((loc, i) => {
        const daily = results[i]?.daily;
        if (!daily?.time) return;
        const cityMap = {};
        daily.time.forEach((dateStr, j) => {
          cityMap[dateStr] = {
            code: daily.weather_code[j],
            high: Math.round(daily.temperature_2m_max[j]),
            low:  Math.round(daily.temperature_2m_min[j]),
            wind: Math.round(daily.wind_speed_10m_max[j]),
          };
        });
        map[loc.name] = cityMap;
      });

      setForecast(map);
    } catch (err) {
      console.error('Forecast fetch failed:', err);
    } finally {
      setLoading(false);
      fetchRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchForecast();
    const interval = setInterval(fetchForecast, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchForecast]);

  return { forecast, loading };
}
